import { apiService } from './apiService';

export interface OnboardingStatus {
  sobriety_set: boolean;
  first_message: boolean;
  first_checkin: boolean;
  first_trail_step: boolean;
  first_diary_note: boolean;
  profile_photo_set: boolean;
  meeting_onboarding: boolean;
}

interface OnboardingStatusResponse {
  success: boolean;
  onboarding: OnboardingStatus;
}

interface OnboardingActionResponse {
  success: boolean;
  action: string;
  completed: boolean;
  completedAt: string;
}

export type OnboardingAction = 
  | 'sobriety_set'
  | 'first_message'
  | 'first_checkin'
  | 'first_trail_step'
  | 'first_diary_note'
  | 'profile_photo_set'
  | 'meeting_onboarding';

class OnboardingService {
  private cachedStatus: OnboardingStatus | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 60000;

  async getStatus(): Promise<OnboardingStatus> {
    const now = Date.now();
    if (this.cachedStatus && now - this.cacheTimestamp < this.CACHE_TTL) {
      return this.cachedStatus;
    }

    try {
      const response = await apiService.get<OnboardingStatusResponse>('/api/onboarding/status');
      
      if (response.success && response.data?.onboarding) {
        this.cachedStatus = response.data.onboarding;
        this.cacheTimestamp = now;
        return response.data.onboarding;
      }
      
      return this.getDefaultStatus();
    } catch (error) {
      console.error('[OnboardingService] Error fetching status:', error);
      return this.getDefaultStatus();
    }
  }

  async markComplete(action: OnboardingAction): Promise<boolean> {
    try {
      if (this.cachedStatus && this.cachedStatus[action]) {
        console.log(`[OnboardingService] Action ${action} already complete, skipping`);
        return true;
      }

      const response = await apiService.patch<OnboardingActionResponse>(`/api/onboarding/${action}`);
      
      if (response.success) {
        if (this.cachedStatus) {
          this.cachedStatus[action] = true;
        }
        console.log(`[OnboardingService] Marked ${action} as complete`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`[OnboardingService] Error marking ${action} complete:`, error);
      return false;
    }
  }

  clearCache(): void {
    this.cachedStatus = null;
    this.cacheTimestamp = 0;
  }

  private getDefaultStatus(): OnboardingStatus {
    return {
      sobriety_set: false,
      first_message: false,
      first_checkin: false,
      first_trail_step: false,
      first_diary_note: false,
      profile_photo_set: false,
      meeting_onboarding: false,
    };
  }
}

export const onboardingService = new OnboardingService();
export default onboardingService;
